/** The two facts about a block that are invisible in its content.
 *
 *  **Pack status.** A block the pack wrote tracks the library: the next
 *  `pack_seed` updates it in place. Editing it opts that row out — the seed will
 *  report it as diverged and leave it alone thereafter. Someone about to make
 *  that edit should be able to see they are making it, which is the whole reason
 *  `source_hash` is on the row.
 *
 *  **Shadowing.** A reference to a system block resolves to the delivering
 *  tenant's own block of the same codename, so the two scopes read differently:
 *  on a system row, `shadowed_by` is how many tenants will NOT see an edit made
 *  here; on a tenant row, `overrides_system` says this row is what actually
 *  renders for that tenant.
 */
import { Chip, Stack, Tooltip } from '@mui/material';

import type { NotificationBlockListItem } from '@/api/notificationBlocks';

type Block = Pick<NotificationBlockListItem, 'pack' | 'shadowing' | 'tenant' | 'codename'>;

export function PackChip({ block }: { block: Block }) {
  if (!block.pack) return null;
  const managed = block.pack.status === 'managed';
  const version = block.pack.version ? ` ${block.pack.version}` : '';
  return (
    <Tooltip
      title={
        managed
          ? `Written by the notification pack${version}. The next pack_seed updates it in place — editing it here opts this block out of future content updates.`
          : `Written by the notification pack${version}, then edited here. pack_seed now reports it and leaves it alone; it takes an explicit --force to bring it back in line.`
      }
    >
      <Chip
        size="small"
        variant="outlined"
        color={managed ? 'success' : 'warning'}
        label={managed ? `pack${version}` : 'pack · edited'}
      />
    </Tooltip>
  );
}

export function ShadowChip({ block }: { block: Block }) {
  const { shadowing } = block;
  if (!shadowing) return null;

  if (block.tenant) {
    if (!shadowing.overrides_system) return null;
    return (
      <Tooltip
        title={`This tenant has its own '${block.codename}', so it renders instead of the system block of that name.`}
      >
        <Chip size="small" variant="outlined" color="info" label="overrides system" />
      </Tooltip>
    );
  }

  if (!shadowing.shadowed_by) return null;
  return (
    <Tooltip
      title={`${shadowing.shadowed_by} tenant${
        shadowing.shadowed_by === 1 ? ' has' : 's have'
      } their own '${block.codename}' and will not see changes made here.`}
    >
      <Chip
        size="small"
        variant="outlined"
        color="info"
        label={`shadowed ×${shadowing.shadowed_by}`}
      />
    </Tooltip>
  );
}

export function BlockProvenanceChips({ block }: { block: Block }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <PackChip block={block} />
      <ShadowChip block={block} />
    </Stack>
  );
}
